export const common = {
  nav: {
    platform: 'Platform',
    solutions: 'Solutions',
    resources: 'Resources',
    company: 'Company',
    requestDemo: 'Request Demo',
    // Platform dropdown
    dashboard: 'Dashboard',
    analytics: 'Analytics',
    monitoring: 'Monitoring',
    compliance: 'Compliance',
    // Solutions dropdown
    iotDevices: 'IoT Devices',
    dataManagement: 'Data Management',
    aiAlerts: 'AI Alerts',
    cbamCompliance: 'CBAM Compliance',
    // Resources dropdown
    documentation: 'Documentation',
    apiReference: 'API Reference',
    caseStudies: 'Case Studies',
    blog: 'Blog',
    // Company dropdown
    about: 'About Us',
    careers: 'Careers',
    contact: 'Contact',
    privacy: 'Privacy Policy',
  },
  hero: {
    badge: 'Industrial IoT Platform',
    title1: 'Digital Transformation',
    title2: 'Real-Time Monitoring',
    title3: 'Carbon Compliance',
    subtitle: 'Empower your industrial operations with AI-driven insights, real-time data processing, and EU carbon footprint compliance solutions',
    startTrial: 'Start Free Trial',
    watchDemo: 'Watch Demo',
    scrollExplore: 'Scroll to Explore',
    // Statistics
    stat1Value: '500+',
    stat1Label: 'Enterprise Clients',
    stat2Value: '50,000+',
    stat2Label: 'Connected Devices',
    stat3Value: '10M+',
    stat3Label: 'Data Points / Day',
    stat4Value: '99.9%',
    stat4Label: 'Uptime Guarantee'
  },
  features: {
    sectionTitle: 'Core Solutions',
    sectionSubtitle: 'Comprehensive platform for industrial digital transformation',
    feature1Title: 'Device Monitoring',
    feature1Desc: 'Real-time monitoring of industrial equipment with advanced sensor networks and IoT connectivity. Support Modbus, RS485, OPC-UA, Snap7 and 15+ industrial protocols with 20 years of field experience.',
    feature2Title: 'Data Lifecycle Management',
    feature2Desc: 'Complete data flow from industrial devices through smart edge processing to cloud — telemetry capture, local processing, transmission, ETL, data bus, storage and presentation.',
    feature3Title: 'AI-Powered Alerts',
    feature3Desc: 'Dual-mode alerting: define your own threshold & condition rules, or let AI automatically detect anomalies and abnormal patterns — get notified before issues escalate.',
    feature4Title: 'CBAM Compliance',
    feature4Desc: 'EU Carbon Border Adjustment Mechanism compliance solutions with automated reporting',
    learnMore: 'Learn More'
  },
  dashboard: {
    badge: 'Live Dashboard Preview',
    title: 'Monitor Everything in Real-Time',
    subtitle: 'Customizable dashboards with drag-and-drop widgets, real-time alerts, and comprehensive data visualization',
    metric1: '99.9%',
    metric1Label: 'Uptime',
    metric2: '<50ms',
    metric2Label: 'Response Time',
    metric3: '10K+',
    metric3Label: 'Data Points/sec',
    exploreDashboard: 'Explore Dashboard'
  },
  compliance: {
    sectionTitle: 'CBAM Compliance Made Simple',
    sectionSubtitle: 'Automated carbon footprint tracking and EU compliance documentation',
    card1Title: 'Automated Reporting',
    card1Desc: 'Generate compliance reports automatically with real-time carbon emission data',
    card2Title: 'Carbon Footprint Tracking',
    card2Desc: 'Track and analyze carbon emissions across your entire production chain',
    card3Title: 'Compliance Documentation',
    card3Desc: 'Pre-built templates for EU CBAM compliance documentation and certification',
    downloadGuide: 'Download Compliance Guide',
    viewDetails: 'Learn More'
  },
  cbam: {
    badge: 'EU CBAM Digital Requirements',
    modalTitle: 'CBAM Digital System Core Requirements',
    modalSubtitle: 'From data hierarchy to MRV full-process digitalization — meeting EU carbon border compliance',
    tab1: 'Data Hierarchy: Org → Product/Line Level',
    tab2: 'MRV Full-Process Digitalization',

    dataLayerIntroTitle: 'CBAM requires product-level emissions, not corporate totals',
    dataLayerIntroDesc: 'CBAM mandates reporting of embedded emissions per ton of product, including direct + indirect emissions, production-line/process-level allocation, and actual measured values as priority over estimates.',

    dataHierarchyTitle: 'Three-Level Data Hierarchy',
    level1Label: 'Level 1 — Organization',
    level1Title: 'Corporate / Group Level',
    level1Desc: 'Aggregate total energy consumption and carbon emissions at the enterprise level. Suitable for annual sustainability reports and ESG disclosures, but insufficient for CBAM product-level requirements.',
    level1Limit: 'Limitation: Cannot allocate emissions to specific products or batches — does not meet CBAM requirements.',
    level2Label: 'Level 2 — Facility',
    level2Title: 'Plant / Production Line Level',
    level2Desc: 'Track energy and emissions by production line or process unit. Enables allocation of carbon costs to product categories and supports process-level KPI management.',
    level2Limit: 'Partial compliance: Supports line-level allocation but may lack real-time granularity for batch tracing.',
    level3Label: 'Level 3 — Device',
    level3Title: 'Equipment / Batch Level (CBAM Core)',
    level3Desc: 'Real-time data acquisition from DCS/SCADA/PLC at the equipment level. Enables precise energy and emission attribution per product batch, meeting CBAM\'s actual measurement priority requirement.',
    level3Limit: 'Full compliance: Real-time measurement + batch tracing + product-level allocation — the gold standard for CBAM.',
    cbamRequired: 'CBAM Required',

    deviceCapTitle: 'BridgeAI Device-Level Acquisition Capability',
    deviceCapDesc: 'BridgeAI connects directly to DCS, SCADA, and PLC systems via Modbus, OPC-UA, Snap7 and 15+ industrial protocols. Energy and emission data is captured per equipment, per batch, and automatically allocated to products — turning device-level data into CBAM-compliant evidence.',

    mrvIntroTitle: 'EU MRV Regulation: Three Core Requirements for Carbon Data',
    mrvIntroDesc: 'The EU Monitoring, Reporting and Verification (MRV) framework requires that all carbon emission data submitted for CBAM must be traceable, verifiable, and tamper-proof. Digital systems must provide end-to-end evidence chains.',

    mrvPillarsTitle: 'MRV Three Pillars',
    mrv1Title: 'Traceable — Data Source Auditable',
    mrv1Desc: 'Every data point must be traceable back to its source instrument. Calibration records, sensor metadata, and measurement timestamps must be stored and queryable. BridgeAI automatically logs device calibration status and data provenance for every reading.',
    mrv1Tag1: 'Instrument Calibration Records',
    mrv1Tag2: 'Sensor Metadata',
    mrv1Tag3: 'Measurement Timestamps',
    mrv2Title: 'Verifiable — Third-Party Audit Ready',
    mrv2Desc: 'Data must support on-site verification by accredited third-party bodies. BridgeAI provides structured audit exports, data replay, and role-based access for verifiers — enabling seamless third-party review without disrupting operations.',
    mrv2Tag1: 'Structured Audit Export',
    mrv2Tag2: 'Data Replay',
    mrv2Tag3: 'Verifier Access Control',
    mrv3Title: 'Tamper-Proof — Blockchain Notarization',
    mrv3Desc: 'Emission records must be immutable. BridgeAI supports blockchain-based notarization of critical data snapshots — creating cryptographic proof that records have not been altered since submission, satisfying EU anti-tampering requirements.',
    mrv3Tag1: 'Blockchain Notarization',
    mrv3Tag2: 'Cryptographic Hash',
    mrv3Tag3: 'Immutable Audit Trail',

    mrvFlowTitle: 'MRV Digital Evidence Chain',
    mrvFlow1: 'Device\nMeasurement',
    mrvFlow2: 'Secure\nStorage',
    mrvFlow3: 'Blockchain\nNotarization',
    mrvFlow4: 'Compliance\nReport',
    mrvFlow5: 'Third-Party\nVerification',

    stat1Val: '100%',
    stat1Label: 'Data Traceability',
    stat2Val: 'ISO 14064',
    stat2Label: 'Verification Ready',
    stat3Val: 'Blockchain',
    stat3Label: 'Tamper-Proof',
  },
  testimonials: {
    sectionTitle: 'Trusted by Industry Leaders',
    review1Title: 'Game-changing platform',
    review1Text: 'BridgeAI transformed our manufacturing operations. Real-time monitoring and AI alerts reduced downtime by 40%.',
    review1Name: 'Michael Chen',
    review1Position: 'Operations Director, TechManufacturing Co.',
    review2Title: 'CBAM compliance simplified',
    review2Text: 'The automated compliance reporting saved us countless hours. EU CBAM documentation is now effortless.',
    review2Name: 'Sarah van der Berg',
    review2Position: 'Sustainability Manager, GreenIndustries NL',
    review3Title: 'Exceptional data insights',
    review3Text: 'The analytics capabilities are outstanding. We can now predict equipment failures weeks in advance.',
    review3Name: 'Klaus Müller',
    review3Position: 'CTO, Industrie Solutions GmbH',
    review4Title: 'Seamless ERP integration',
    review4Text: 'Integration with our existing ERP system was smooth. The multi-language support is perfect for our global team.',
    review4Name: 'Emma Johnson',
    review4Position: 'IT Director, GlobalTech Industries',
    review5Title: 'Outstanding support',
    review5Text: 'The customizable dashboards and role-based permissions fit perfectly with our organizational structure.',
    review5Name: 'Pierre Dubois',
    review5Position: 'Plant Manager, EuroManufacturing SA',
    review6Title: 'ROI in 3 months',
    review6Text: 'We saw measurable ROI within three months. The predictive maintenance alone paid for the platform.',
    review6Name: 'Anna Kowalski',
    review6Position: 'CFO, Industrial Dynamics Ltd.'
  },
  cta: {
    title: 'Ready to Transform Your Operations?',
    subtitle: 'Start your 30-day free trial, no credit card required',
    getStarted: 'Get Started Now'
  },
  footer: {
    tagline: 'Empowering industrial digital transformation',
    platformTitle: 'Platform',
    dashboard: 'Dashboard',
    analytics: 'Analytics',
    monitoring: 'Monitoring',
    compliance: 'Compliance',
    solutionsTitle: 'Solutions',
    iotDevices: 'IoT Devices',
    dataManagement: 'Data Management',
    aiAlerts: 'AI Alerts',
    cbamCompliance: 'CBAM Compliance',
    resourcesTitle: 'Resources',
    documentation: 'Documentation',
    apiReference: 'API Reference',
    caseStudies: 'Case Studies',
    blog: 'Blog',
    companyTitle: 'Company',
    about: 'About Us',
    careers: 'Careers',
    contact: 'Contact',
    privacy: 'Privacy Policy',
    stayConnected: 'Stay Connected',
    newsletterText: 'Get platform updates and industry insights',
    emailPlaceholder: 'Enter your email',
    subscribe: 'Subscribe',
    subscribing: 'Subscribing...',
    subscribeSuccess: 'Thank you for subscribing!',
    errorInvalidEmail: 'Please enter a valid email address',
    errorSubmission: 'Subscription failed. Please try again.',
    copyright: '© 2026 BridgeAI. All rights reserved.'
  },
  dataLifecycle: {
    modalTitle: 'Data Lifecycle Management',
    modalSubtitle: 'End-to-end industrial data flow — from device to dashboard',
    flowLabel: 'Full Data Flow',
    flowTitle: 'From Industrial Device to Insight',
    flowDesc: 'BridgeAI manages the complete data journey: capturing raw telemetry from field devices, processing at the edge, transmitting securely to the cloud, and delivering actionable insights through dashboards and APIs.',
    step1Title: 'Industrial Devices',
    step1Desc: 'Connect PLCs, drives, meters and controllers via Modbus, RS485, OPC-UA, Snap7, PROFINET and more.',
    step2Title: 'Sensor Telemetry Capture',
    step2Desc: 'Collect temperature, pressure, vibration, flow, power and custom sensor data at high frequency.',
    step3Title: 'Smart Edge Processing',
    step3Desc: 'Local edge device filters, aggregates and compresses data before transmission — reducing bandwidth by up to 80%.',
    step4Title: 'Secure Transmission',
    step4Desc: 'Send data to the cloud via MQTT, custom TCP/UDP, HTTP/REST or WebSocket with end-to-end TLS encryption.',
    step5Title: 'ETL Pipeline',
    step5Desc: 'Extract, transform and load data with validation and enrichment to ensure quality and consistency.',
    step6Title: 'Data Bus',
    step6Desc: 'Distribute data streams via Kafka or RabbitMQ event bus to decouple producers and consumers.',
    step7Title: 'Storage Layer',
    step7Desc: 'Persist data in PostgreSQL for relational records, TimescaleDB / InfluxDB for time-series, and S3 for archives.',
    step8Title: 'Data Pipelines',
    step8Desc: 'Route data through normal processing, AI inference, alert evaluation and report generation pipelines.',
    step9Title: 'Web & App Presentation',
    step9Desc: 'Deliver insights via real-time web dashboards, mobile apps, REST APIs, webhooks and ERP integrations.',
    pipelinesTitle: 'Processing Pipeline Types',
    pipeline1Title: 'Normal Processing Pipeline',
    pipeline1Desc: 'Standard data flow for aggregation, KPI calculation, threshold alerting and scheduled report generation. Handles the majority of operational data with low latency and high reliability.',
    pipeline2Title: 'AI Processing Pipeline',
    pipeline2Desc: 'Machine learning inference pipeline for anomaly detection, predictive maintenance and pattern recognition. Models are trained on historical data and updated continuously.',
    stat1: 'End-to-End Latency',
    stat2: 'Data Points / sec',
    stat3: 'Platform Uptime',
    stat4: 'Industrial Protocols',
  },
  aiAlerts: {
    modalTitle: 'AI-Powered Alerts',
    modalSubtitle: 'Dual-mode alerting: user-defined rules + AI automatic anomaly detection',
    badge: 'Intelligent Alert Engine',
    flowTitle: 'Stay Ahead of Every Issue',
    flowDesc: 'BridgeAI combines user-defined rule-based alerts with AI-driven anomaly detection — so you get notified the moment something goes wrong, or even before it does.',
    flowLabel: 'Alert Processing Flow',
    flow1: 'Sensor Data',
    flow2: 'Rule Engine',
    flow3: 'AI Analysis',
    flow4: 'Alert Triggered',
    flow5: 'Notification',
    flow6: 'Action / Fix',
    dualModeLabel: 'Dual Alert Modes',
    rule1Title: 'User-Defined Rule Alerts',
    rule1Desc: 'Create custom alert rules based on thresholds, conditions, time windows and multi-metric logic. Full control over when and how you get notified.',
    rule2Title: 'AI Automatic Anomaly Detection',
    rule2Desc: 'AI continuously learns your equipment\'s normal behavior and automatically raises alerts when it detects anomalies, abnormal patterns or early signs of failure — no rules needed.',
    featuresLabel: 'Alert Capabilities',
    feat1Title: 'Multi-Channel Notifications',
    feat1Desc: 'Deliver alerts via email, SMS, webhook, PagerDuty or Slack — reach the right person instantly.',
    feat2Title: 'Predictive Insights',
    feat2Desc: 'AI trend analysis and forecasting help you understand root causes and predict future issues before they occur.',
    feat3Title: 'Alert History & Audit',
    feat3Desc: 'Full alert log with audit trail, replay and export — complete visibility into every event.',
    feat4Title: 'Smart Alert Management',
    feat4Desc: 'Priority levels, escalation policies, suppression and grouping to reduce noise and focus on what matters.',
    stat1: 'Alert Latency',
    stat2: 'Detection Accuracy',
    stat3: 'Rule Types',
    stat4: 'Notification Channels',
  },
  cbamModal: {
    badge: 'EU CBAM Digital Requirements',
    modalTitle: 'CBAM Digital System Core Requirements',
    modalSubtitle: 'From data hierarchy to MRV full-process digitalization — meeting EU carbon border compliance',
    tab1: 'Data Hierarchy: Org → Product/Line Level',
    tab2: 'MRV Full-Process Digitalization',

    dataLayerIntroTitle: 'CBAM requires product-level emissions, not corporate totals',
    dataLayerIntroDesc: 'CBAM mandates reporting of embedded emissions per ton of product, including direct + indirect emissions, production-line/process-level allocation, and actual measured values as priority over estimates.',

    dataHierarchyTitle: 'Three-Level Data Hierarchy',
    level1Label: 'Level 1 — Organization',
    level1Title: 'Corporate / Group Level',
    level1Desc: 'Aggregate total energy consumption and carbon emissions at the enterprise level. Suitable for annual sustainability reports and ESG disclosures, but insufficient for CBAM product-level requirements.',
    level1Limit: 'Limitation: Cannot allocate emissions to specific products or batches — does not meet CBAM requirements.',
    level2Label: 'Level 2 — Facility',
    level2Title: 'Plant / Production Line Level',
    level2Desc: 'Track energy and emissions by production line or process unit. Enables allocation of carbon costs to product categories and supports process-level KPI management.',
    level2Limit: 'Partial compliance: Supports line-level allocation but may lack real-time granularity for batch tracing.',
    level3Label: 'Level 3 — Device',
    level3Title: 'Equipment / Batch Level (CBAM Core)',
    level3Desc: 'Real-time data acquisition from DCS/SCADA/PLC at the equipment level. Enables precise energy and emission attribution per product batch, meeting CBAM\'s actual measurement priority requirement.',
    level3Limit: 'Full compliance: Real-time measurement + batch tracing + product-level allocation — the gold standard for CBAM.',
    cbamRequired: 'CBAM Required',

    deviceCapTitle: 'BridgeAI Device-Level Acquisition Capability',
    deviceCapDesc: 'BridgeAI connects directly to DCS, SCADA, and PLC systems via Modbus, OPC-UA, Snap7 and 15+ industrial protocols. Energy and emission data is captured per equipment, per batch, and automatically allocated to products — turning device-level data into CBAM-compliant evidence.',

    mrvIntroTitle: 'EU MRV Regulation: Three Core Requirements for Carbon Data',
    mrvIntroDesc: 'The EU Monitoring, Reporting and Verification (MRV) framework requires that all carbon emission data submitted for CBAM must be traceable, verifiable, and tamper-proof. Digital systems must provide end-to-end evidence chains.',

    mrvPillarsTitle: 'MRV Three Pillars',
    mrv1Title: 'Traceable — Data Source Auditable',
    mrv1Desc: 'Every data point must be traceable back to its source instrument. Calibration records, sensor metadata, and measurement timestamps must be stored and queryable. BridgeAI automatically logs device calibration status and data provenance for every reading.',
    mrv1Tag1: 'Instrument Calibration Records',
    mrv1Tag2: 'Sensor Metadata',
    mrv1Tag3: 'Measurement Timestamps',
    mrv2Title: 'Verifiable — Third-Party Audit Ready',
    mrv2Desc: 'Data must support on-site verification by accredited third-party bodies. BridgeAI provides structured audit exports, data replay, and role-based access for verifiers — enabling seamless third-party review without disrupting operations.',
    mrv2Tag1: 'Structured Audit Export',
    mrv2Tag2: 'Data Replay',
    mrv2Tag3: 'Verifier Access Control',
    mrv3Title: 'Tamper-Proof — Blockchain Notarization',
    mrv3Desc: 'Emission records must be immutable. BridgeAI supports blockchain-based notarization of critical data snapshots — creating cryptographic proof that records have not been altered since submission, satisfying EU anti-tampering requirements.',
    mrv3Tag1: 'Blockchain Notarization',
    mrv3Tag2: 'Cryptographic Hash',
    mrv3Tag3: 'Immutable Audit Trail',

    mrvFlowTitle: 'MRV Digital Evidence Chain',
    mrvFlow1: 'Device\nMeasurement',
    mrvFlow2: 'Secure\nStorage',
    mrvFlow3: 'Blockchain\nNotarization',
    mrvFlow4: 'Compliance\nReport',
    mrvFlow5: 'Third-Party\nVerification',

    stat1Val: '100%',
    stat1Label: 'Data Traceability',
    stat2Val: 'ISO 14064',
    stat2Label: 'Verification Ready',
    stat3Val: 'Blockchain',
    stat3Label: 'Tamper-Proof',
  },
  contactForm: {
    title: 'Request a Demo',
    fields: {
      name: 'Full Name',
      company: 'Company Name',
      email: 'Email Address',
      phone: 'Phone Number',
      message: 'Message'
    },
    placeholders: {
      name: 'Enter your full name',
      company: 'Enter your company name',
      email: 'Enter your email address',
      phone: 'Enter your phone number',
      message: 'Tell us about your requirements...'
    },
    errors: {
      nameRequired: 'Name is required',
      companyRequired: 'Company name is required',
      emailRequired: 'Email is required',
      emailInvalid: 'Please enter a valid email address',
      phoneRequired: 'Phone number is required',
      messageRequired: 'Message is required',
      messageTooLong: 'Message cannot exceed 500 characters'
    },
    submit: 'Submit Request',
    submitting: 'Submitting...',
    successTitle: 'Thank You!',
    successMessage: 'We have received your request and will contact you shortly.'
  },
  about: {
    title: 'About Us',
    introTitle: 'Who We Are',
    introText: 'BridgeAI is a leading provider of industrial IoT and digital transformation solutions. We help manufacturers optimize operations, reduce carbon footprint, and achieve EU compliance through cutting-edge technology and AI-driven insights.',
    missionTitle: 'Our Mission',
    missionText: 'To empower industrial enterprises with intelligent, data-driven solutions that drive operational excellence and sustainable growth.',
    visionTitle: 'Our Vision',
    visionText: 'To become the global standard for industrial digital transformation, enabling a smarter, greener, and more efficient manufacturing future.',
    valuesTitle: 'Core Values',
    value1Title: 'Innovation',
    value1Text: 'Continuously pushing the boundaries of industrial IoT and AI technology.',
    value2Title: 'Reliability',
    value2Text: 'Delivering robust, secure, and mission-critical solutions our customers can trust.',
    value3Title: 'Sustainability',
    value3Text: 'Committed to helping industries reduce environmental impact and achieve carbon neutrality.',
    value4Title: 'Partnership',
    value4Text: 'Building long-term relationships based on mutual success and shared values.',
    teamTitle: 'Our Team',
    teamText: 'Our team consists of industry veterans, data scientists, and IoT specialists with decades of combined experience in manufacturing, automation, and enterprise software. We bring deep domain expertise and a passion for solving complex industrial challenges.',
    teamExperts: 'Industry Experts',
    teamYears: 'Years Experience',
    teamProjects: 'Projects Delivered'
  },
  contact: {
    title: 'Contact Us',
    infoTitle: 'Get in Touch',
    infoText: 'Have questions or want to learn more about BridgeAI? Our team is here to help. Reach out to us through any of the channels below.',
    addressTitle: 'Office Address',
    addressText: 'Hengelosestraat 581, 7581 AG Enschede, Netherlands',
    emailTitle: 'Email',
    phoneTitle: 'Phone',
    hoursTitle: 'Business Hours',
    hoursText: 'Monday - Friday: 9:00 AM - 6:00 PM CET',
    formTitle: 'Send Us a Message',
    name: 'Full Name',
    company: 'Company Name',
    email: 'Email Address',
    phone: 'Phone Number',
    message: 'Message',
    namePlaceholder: 'Enter your full name',
    companyPlaceholder: 'Enter your company name',
    emailPlaceholder: 'Enter your email address',
    phonePlaceholder: 'Enter your phone number',
    messagePlaceholder: 'Tell us how we can help...',
    errorName: 'Name is required',
    errorCompany: 'Company name is required',
    errorEmail: 'Email is required',
    errorEmailInvalid: 'Please enter a valid email address',
    errorPhone: 'Phone number is required',
    errorMessage: 'Message is required',
    errorMessageLength: 'Message cannot exceed 500 characters',
    submit: 'Send Message',
    submitting: 'Sending...',
    successMessage: 'Thank you! Your message has been sent successfully.'
  },
  privacy: {
    title: 'Privacy Policy',
    lastUpdated: 'Last Updated:',
    lastUpdatedDate: 'January 15, 2026',
    introTitle: 'Introduction',
    introText: 'BridgeAI ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our industrial IoT platform and services.',
    collectionTitle: 'Information We Collect',
    collectionText: 'We collect information that you provide directly to us, including:',
    collectionItem1: 'Personal identification information (name, email address, phone number, company name)',
    collectionItem2: 'Account credentials and authentication data',
    collectionItem3: 'Industrial device data, sensor readings, and telemetry information',
    collectionItem4: 'Usage data, log files, and analytics information',
    usageTitle: 'How We Use Your Information',
    usageText: 'We use the information we collect to:',
    usageItem1: 'Provide, maintain, and improve our platform and services',
    usageItem2: 'Process transactions and send related information',
    usageItem3: 'Send technical notices, updates, security alerts, and support messages',
    usageItem4: 'Respond to your comments, questions, and customer service requests',
    storageTitle: 'Data Storage and Security',
    storageText: 'We implement industry-standard security measures to protect your data, including encryption, access controls, and regular security audits. Your industrial data is stored in secure, EU-compliant data centers with redundancy and backup systems.',
    sharingTitle: 'Information Sharing',
    sharingText: 'We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:',
    sharingNote: 'Important:',
    sharingNoteText: 'We will never share your industrial device data or proprietary information without your explicit consent.',
    rightsTitle: 'Your Rights',
    rightsText: 'Under GDPR and applicable data protection laws, you have the following rights:',
    rightAccessTitle: 'Right to Access',
    rightAccessText: 'Request copies of your personal data',
    rightCorrectTitle: 'Right to Rectification',
    rightCorrectText: 'Request correction of inaccurate data',
    rightDeleteTitle: 'Right to Erasure',
    rightDeleteText: 'Request deletion of your personal data',
    rightPortabilityTitle: 'Right to Data Portability',
    rightPortabilityText: 'Request transfer of your data to another service',
    cookiesTitle: 'Cookies and Tracking',
    cookiesText: 'We use cookies and similar tracking technologies to track activity on our platform and store certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.',
    contactTitle: 'Contact Us',
    contactText: 'If you have any questions about this Privacy Policy or wish to exercise your data protection rights, please contact us at:'
  }
};
